import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TimeSpanSelector } from "../../components/ui/time-span-selector";
import { AnalyticsSearchOptions } from "./analytics";
import Select, { SingleValue } from "react-select";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import FilterDropdown from "../../components/ui/filterDropdown";
import FilterModalDropdown from "../../components/ui/filterModalDropdown";
import { useAppSelector } from "../../reducers/store";

interface Props {
  searchOptions: AnalyticsSearchOptions;
  setSearchOptions: (searchOptions: AnalyticsSearchOptions) => void;
  processRequest: () => void;
  chartRef: React.RefObject<any>;
}

export function AnalyticsOptionsHeader(props: Props) {
  const [searchParams] = useSearchParams();

  const selectedSupplier = useAppSelector((state) => state.supplier.selectedSupplier);
  const selectedProducingFacilities = useAppSelector((state) => state.supplier.selectedProducingFacilities);
  const selectedProducingFacilitiesFiltered = useAppSelector((state) => state.supplier.selectedProducingFacilitiesFiltered);
  const initialLoadComplete = useAppSelector((state) => state.supplier.initialLoadComplete);

  // grabbing all chart data for user
  const objectOptions = [
    { label: "Quality Alerts", value: "Quality Alerts" },
    { label: "Near Misses", value: "Near Misses" },
    { label: "All Quality Issues", value: "All Quality Issues" },
    { label: "Proactive Audits", value: "Proactive Audits" },
    { label: "Additional Hours", value: "Additional Hours" },
  ];

  const groupOptions = [
    { label: "Producing Facility", value: "Producing Facility" },
    { label: "Part Number", value: "Part Number" },
    { label: "Issue Type", value: "Issue Type" },
    { label: "Location", value: "Location" },
    { label: "Representative", value: "Representative" },
    { label: "OEM", value: "OEM" },
    { label: "No Grouping", value: "No Grouping" },
  ];

  const calculationOptions = [
    { label: "Total", value: "Total" },
    { label: "Percentage", value: "Percentage" },
    { label: "Average", value: "Average" },
  ];

  const timeOptions = [
    { label: "Days", value: "Days" },
    { label: "Weeks", value: "Weeks" },
    { label: "Months", value: "Months" },
  ];

  const chartOptions = [
    {
      label: "Area",
      value: "Area",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          className="size-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5m.75-9 3-3 2.148 2.148A12.061 12.061 0 0 1 16.5 7.605"
          />
        </svg>
      ),
    },
    {
      label: "Line",
      value: "Line",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          className="size-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941"
          />
        </svg>
      ),
    },
    {
      label: "Bar",
      value: "Bar",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          className="size-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"
          />
        </svg>
      ),
    },
    {
      label: "Pie",
      value: "Pie",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          className="size-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10.5 6a7.5 7.5 0 1 0 7.5 7.5h-7.5V6Z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13.5 10.5H21A7.5 7.5 0 0 0 13.5 3v7.5Z"
          />
        </svg>
      ),
    },
    {
      label: "Table",
      value: "Table",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          className="size-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5"
          />
        </svg>
      ),
    },
    {
      label: "Number",
      value: "Number",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          className="size-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5.25 8.25h15m-16.5 7.5h15m-1.8-13.5-3.9 19.5m-2.1-19.5-3.9 19.5"
          />
        </svg>
      ),
    },
  ];
  const CustomOption = (props: any) => {
    const { data, innerRef, innerProps } = props;
    return (
      <div ref={innerRef} {...innerProps} className="flex items-center p-2">
        {data.icon}
        <span className="ml-2">{data.label}</span>
      </div>
    );
  };

  const CustomSingleValue = (props: any) => {
    const { data } = props;
    return (
      <div className="flex items-center justify-center">
        {data.icon}
        <span className="ml-2">{data.label}</span>
      </div>
    );
  };

  // Download using ref
  const downloadChart = () => {
    if (props.chartRef.current) {
      const url = props.chartRef.current.toBase64Image();
      const link = document.createElement("a");
      link.href = url;
      link.download = "chart.png";
      link.click();
    } else {
      console.error(
        "Chart ref is not defined or the chart hasn't rendered yet.",
      );
    }
  };

  const processRequest = async () => {
    // Call process request once the search options are set
    setTimeout(() => {
      if (selectedSupplier && selectedSupplier.id) {
        props.processRequest();
      }
    }, 0);
  };

  useEffect(() => {
    const options = { ...props.searchOptions };

    // Set seach options from the URL
    for (const [key, value] of Object.entries(options)) {
      if (searchParams.has(key)) {
        var data = searchParams.get(key) ?? "";
        options[key as keyof AnalyticsSearchOptions] =
          data.length > 0 && data !== "null" && data !== "undefined"
            ? data
            : (null as any);
      }
    }

    props.setSearchOptions(options);

    processRequest();
  }, []);

  useEffect(() => {
    if (initialLoadComplete) {
      processRequest();
    }
  }, [
    selectedSupplier,
    selectedProducingFacilitiesFiltered,
    initialLoadComplete,
  ]);

  useEffect(() => {
    // Set the search options in the URL
    var params = ``;

    for (const [key, value] of Object.entries(props.searchOptions)) {
      params += `&${key}=${value}`;
    }

    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${params.replace("&", "?")}`,
    );
  }, [props.searchOptions]);

  const setFilter = (
    option: keyof AnalyticsSearchOptions,
    e: SingleValue<{ label: string; value: string | null }>,
    customData?: { customStartDate: string; customEndDate: string },
  ) => {
    const searchOptions = { ...props.searchOptions };

    searchOptions[option] = e?.value ?? "";

    if (option === "timePeriod" && e?.value !== "custom") {
      searchOptions["customStartDate"] = null;
      searchOptions["customEndDate"] = null;
    } else if (option === "timePeriod" && e?.value === "custom") {
      searchOptions["customStartDate"] = customData?.customStartDate ?? "";
      searchOptions["customEndDate"] = customData?.customEndDate ?? "";
    }
    // If object option is set to 'Additional Hours', update the groupOptions accordingly
    if (option === "type" && e?.value === "Additional Hours") {
      searchOptions.group = "No Grouping"; // You can choose a default option
    }

    switch (option) {
      case "calculation":
        switch (e?.value) {
          case "Total":
            searchOptions.calculation = "Total";
            break;
          case "Percentage":
            searchOptions.calculation = "Percentage";
            break;
          case "Average":
            searchOptions.calculation = "Average";
            break;
          default:
            searchOptions.calculation = "Total";
            break;
        }
    }

    props.setSearchOptions(searchOptions);
  };

  // For filtering group options based on the selected object option
  const filterGroupOptions = (selectedObjectOption: string) => {
    const alertOptions = ["All Quality Issues"];
    let filteredGroupOptions = groupOptions;
    if (selectedObjectOption === "Additional Hours") {
      // Return all group options except 'Part Number'
      filteredGroupOptions = filteredGroupOptions.filter((option) => option.value !== "Part Number");
    }
    if (!alertOptions.includes(selectedObjectOption)) {
      filteredGroupOptions = filteredGroupOptions.filter((option) => option.value !== "Issue Type");
      if(props.searchOptions.group === "Issue Type") {
        setFilter("group", { label: "No Grouping", value: "No Grouping" });
      }
    }
    // Return all group options if 'Additional Hours' is not selected
    return filteredGroupOptions;
  };

  const handleTimeSpanChange = (
    data: string,
    customStartDate?: string,
    customEndDate?: string,
  ) => {
    if (customStartDate && customEndDate) {
      setFilter(
        "timePeriod",
        { label: data, value: "custom" },
        { customStartDate, customEndDate },
      );
    } else if (data !== "custom") {
      setFilter("timePeriod", { label: data, value: data });
    }
  };

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      border: "1px solid transparent", // Transparent border for the visual effect
      boxShadow: "0 0 5px rgba(0, 0, 0, 0.3)", // Blurred border effect
      backdropFilter: "blur(1px)", // Slightly reduced blur effect
      backgroundColor: "rgba(255, 255, 255, 0.1)", // Semi-transparent background
      paddingRight: "2px",
      paddingLeft: "4px",
      cursor: "pointer",

      height: "37px", // Set desired height
      minHeight: "36px", // Ensure minimum height is set
      display: "flex",
      alignItems: "center", // Vertically center the content
      width: "auto", // Allow control to expand based on content
      minWidth: "100px", // Set a minimum width to avoid too small control
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      height: "100%", // Take full height of the control
      display: "flex",
      alignItems: "center", // Vertically center the content
      padding: "0", // Remove padding for a more compact look
    }),
    singleValue: (provided: any) => ({
      ...provided,
      margin: "0", // Remove any margin
      padding: "0", // Remove padding
      display: "flex",
      alignItems: "center", // Vertically center the selected value
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      padding: "0", // Remove padding to ensure it fits within the control
      display: "flex",
      alignItems: "center", // Center the arrow vertically
      justifyContent: "center",
      height: "100%", // Ensure the arrow takes up the full height
    }),
    indicatorSeparator: () => ({
      display: "none", // Hide the separator if not needed
    }),
    menu: (provided: any) => ({
      ...provided,
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)", // Light shadow for the dropdown
      backdropFilter: "blur(1px)", // Apply the blur effect to the dropdown
    }),
  };

  const customStyles1 = {
    control: (provided: any) => ({
      ...provided,
      border: "1px solid transparent", // Transparent border to help with the visual effect
      boxShadow: "0 0 10px rgba(0, 0, 0, 0.3)", // Blurred border effect
      backdropFilter: "blur(1px)", // Slightly reduced blur effect
      backgroundColor: "rgba(255, 255, 255, 0.1)", // Semi-transparent background
      width: "200px",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menu: (provided: any) => ({
      ...provided,
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)", // Light shadow for the dropdown
      backdropFilter: "blur(1px)", // Apply the blur effect to the dropdown
    }),
  };

  return (
    <>
      <div className="flex w-[100%] flex-col gap-y-[12px] text-[#212322]">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center gap-x-[12px]">
            <div>
              <Select
                defaultValue={
                  objectOptions.find(
                    (t) => t.value === props.searchOptions.type,
                  ) ?? objectOptions[0]
                }
                value={
                  objectOptions.find(
                    (t) => t.value === props.searchOptions.type,
                  ) ?? objectOptions[0]
                }
                onChange={(e) => {
                  setFilter("type", e);
                }}
                options={objectOptions}
                styles={customStyles}
                className="text-[15px] font-medium text-[#212322]"
              />
            </div>
            <div>
              <span>grouped by</span>
            </div>
            <div>
              <Select
                defaultValue={
                  groupOptions.find(
                    (t) => t.value === props.searchOptions.group,
                  ) ?? groupOptions[0]
                }
                value={
                  groupOptions.find(
                    (t) => t.value === props.searchOptions.group,
                  ) ?? groupOptions[0]
                }
                onChange={(e) => setFilter("group", e)}
                options={filterGroupOptions(props.searchOptions.type)}
                styles={customStyles1}
                className="text-[15px] font-medium text-[#212322]"
              />
            </div>
            <div>
              <span>shown as</span>
            </div>
          </div>

          <div className="flex flex-row items-center">
            <div className="flex flex-row items-center gap-x-[12px]">
              <div>
                <Select
                  defaultValue={
                    chartOptions.find(
                      (t) => t.value === props.searchOptions.chartType,
                    ) ?? chartOptions[0]
                  }
                  value={
                    chartOptions.find(
                      (t) => t.value === props.searchOptions.chartType,
                    ) ?? chartOptions[0]
                  }
                  onChange={(e) => setFilter("chartType", e)}
                  options={chartOptions}
                  styles={customStyles}
                  isSearchable={false}
                  components={{
                    Option: CustomOption,
                    SingleValue: CustomSingleValue,
                  }}
                  className="cursor-pointer text-[15px] font-medium"
                />
              </div>
              {/* <div>
                <button
                  style={{
                    border: "1px solid transparent", // Transparent border to help with the visual effect
                    boxShadow: "0 0 5px rgba(0, 0, 0, 0.3)", // Blurred border effect
                    backdropFilter: "blur(1px)", // Slightly reduced blur effect
                    backgroundColor: "rgba(255, 255, 255, 0.1)", // Semi-transparent background
                  }}
                  className="flex h-[38px] items-center justify-evenly gap-x-[8px] rounded-md px-[12px]"
                >
                  <FontAwesomeIcon
                    className="text-[14px] text-[#212322]"
                    icon={faFilter}
                  />
                  <span className="text-[14px] text-[#212322]">Filters</span>
                </button>
              </div> */}
              <FilterModalDropdown setSearchOptions={props.setSearchOptions} />
            </div>
            <div className="pl-[22px]">
              <FilterDropdown
                searchOptions={props.searchOptions}
                title="Save report for later"
                content="This is the content"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-row items-center justify-between border-b-[1px] border-[#00000070] pb-[16px]">
          <div className="flex flex-row items-center gap-x-[12px]">
            <div>
              <Select
                defaultValue={
                  calculationOptions.find(
                    (t) => t.value === props.searchOptions.calculation,
                  ) ?? calculationOptions[0]
                }
                value={
                  calculationOptions.find(
                    (t) => t.value === props.searchOptions.calculation,
                  ) ?? calculationOptions[0]
                }
                onChange={(e) => setFilter("calculation", e)}
                options={calculationOptions}
                styles={customStyles}
                className="text-[15px] font-medium text-[#212322]"
              />
            </div>
            <div>
              <span>in increments of</span>
            </div>
            <div>
              <Select
                defaultValue={
                  timeOptions.find(
                    (t) => t.value === props.searchOptions.timeIncrement,
                  ) ?? timeOptions[0]
                }
                value={
                  timeOptions.find(
                    (t) => t.value === props.searchOptions.timeIncrement,
                  ) ?? timeOptions[0]
                }
                styles={customStyles}
                onChange={(e) => setFilter("timeIncrement", e)}
                options={timeOptions}
                className="text-[15px] font-medium text-[#212322]"
              />
            </div>
          </div>

          <div className="flex flex-row items-center">
            <TimeSpanSelector
              selectedDays={props.searchOptions.timePeriod}
              customStartDate={props.searchOptions.customStartDate}
              customEndDate={props.searchOptions.customEndDate}
              handleButtonClick={handleTimeSpanChange}
            />
            <div className="pl-[22px]">
              {props.searchOptions.chartType.toLocaleLowerCase() !== "table" ? (
                <button
                  onClick={downloadChart}
                  className="flex h-[36px] w-[140px] items-center justify-evenly gap-x-[8px] rounded-md border border-gray-300 bg-white px-[12px] shadow-lg"
                  style={{
                    border: "1px solid transparent", // Transparent border to help with the visual effect
                    boxShadow: "0 0 5px rgba(0, 0, 0, 0.3)", // Blurred border effect
                    backdropFilter: "blur(1px)", // Slightly reduced blur effect
                    backgroundColor: "rgba(255, 255, 255, 0.1)", // Semi-transparent background
                  }}
                >
                  <FontAwesomeIcon
                    className="text-[14px] text-[#212322]"
                    icon={faDownload}
                  />
                  <span className="text-[14px] text-[#212322]">Download</span>
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
